/**
 * 由核心擁有的標準節點定義。對應 docs/protocol.md §5。
 *
 * 規則一句話：路徑中任一段以 `$` 開頭的節點由核心擁有，其餘由 app 擁有。
 */

import type { NodeDef, WindowRect } from '@qdesktop/protocol'

export const DEFAULT_RECT: WindowRect = { x: 0, y: 0, w: 640, h: 480 }

/** 每個實例的核心節點（路徑相對於 app/{uid}/） */
export const INSTANCE_KERNEL_NODES: readonly NodeDef[] = [
  {
    path: '$meta',
    kind: 'state',
    access: 'r',
    schema: { type: 'object' },
    description:
      '本實例的完整 manifest：所有節點的路徑、型別、說明與風險等級，以及路徑片段的群組說明。要理解這個 app 能做什麼，讀這一個節點就夠了。',
  },
  {
    path: '$lifecycle',
    kind: 'state',
    access: 'r',
    schema: { type: 'string', enum: ['loading', 'ready', 'crashed', 'closed'] },
    description:
      '本實例的生命週期。唯讀——由核心觀察後寫入，app 不能宣稱自己已被銷毀。crashed 或 closed 時，除了 $window 與 $meta 之外的節點都已失效。',
  },
  {
    path: '$window/rect',
    kind: 'state',
    access: 'rw',
    schema: {
      type: 'object',
      properties: {
        x: { type: 'number' },
        y: { type: 'number' },
        w: { type: 'number', minimum: 0 },
        h: { type: 'number', minimum: 0 },
      },
      additionalProperties: false,
    },
    description:
      '視窗位置與尺寸，單位為像素。支援部分寫入：只給 {"x":100} 就只改 x，其餘保持不變。移動與縮放因此是一次原子更新，訂閱者只會收到一次通知。',
  },
  {
    path: '$window/state',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string', enum: ['normal', 'min', 'max', 'hidden'] },
    description:
      '視窗顯示狀態。hidden 表示不顯示但仍在執行（無頭 app 固定為 hidden）。這與 $lifecycle 不同——這裡描述的是可見性，不是存活狀態。',
  },
  {
    path: '$window/z',
    kind: 'state',
    access: 'rw',
    schema: { type: 'integer' },
    description:
      '視窗疊放順序，數字越大越上層。點擊視窗會自動提到最上層。要用程式把某個視窗帶到最前面，寫一個比目前所有視窗都大的值即可。',
  },
  {
    path: '$window/locked',
    kind: 'state',
    access: 'rw',
    schema: { type: 'boolean' },
    description:
      '釘住這個視窗：true 時 $window/rect 寫不動（**不論來源**，agent 也一樣），標題列與縮放把手一併收起。想移動請先解鎖，否則「鎖住」只是一句沒有效力的宣告。標題列上的 📌 只負責釘：按下去標題列自己就收起來了，所以**解除在桌面底部那顆 📌**——它疊在所有視窗之上，釘住又最大化的視窗蓋不住它。',
  },
  {
    path: '$window/transparent',
    kind: 'state',
    access: 'rw',
    schema: { type: 'boolean' },
    description:
      '拿掉視窗的外框與陰影，讓 app 自己畫的東西直接浮在桌布上——適合疊在畫面上的資訊層。**底色不在這裡管**：桌面本來就不替 app 畫底色，要不要透明是 app 自己頁面 CSS 的事。標題列不受影響，它是拖曳與按鈕的抓手。',
  },
]

/** $sys 命名空間的節點（路徑為絕對路徑） */
export const SYS_NODES: readonly NodeDef[] = [
  {
    path: '$sys/registry/instances',
    kind: 'state',
    access: 'r',
    schema: { type: 'array' },
    description:
      '目前**執行中**的實例摘要清單，含 uid、標題、一句話說明、生命週期、節點數量與來源網址。這是探索的起點：先讀這份摘要決定要用哪個 app，再讀該實例的 $meta 取得完整節點清單。不要一開始就展開所有 app 的全部節點。',
  },
  {
    path: '$sys/registry/apps',
    kind: 'state',
    access: 'r',
    schema: { type: 'array' },
    description:
      '**可以開啟**的 app 目錄，每一項含 app_id、網址、標題與一句話說明。這與 registry/instances 不同——這裡是「有什麼可以開」，那裡是「現在開了什麼」。要開新的 app，從這份目錄挑一個 app_id 傳給 $sys/cmd/launch。',
  },
  {
    path: '$sys/activity',
    kind: 'event',
    access: 'r',
    schema: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['write', 'call'] },
        path: { type: 'string' },
        origin: { type: 'string', nullable: true },
        ok: { type: 'boolean' },
      },
      required: ['kind', 'path', 'ok'],
    },
    example: {
      kind: 'write',
      path: 'app/hr_7f3a2c/config/alarm_high',
      origin: 'agent_1',
      ok: false,
      error: { code: 'E_REJECTED', message: '量測中不得調整警戒值' },
    },
    description:
      '每一次寫入或 action 呼叫的嘗試，**包含被拒絕的**。訂閱一般的 topic 只看得到成功造成的狀態變化，看不到「誰試了什麼但被擋下來」——那正是監看 agent 時最需要的資訊。origin 是發起者的實例 uid，由傳輸層填入，呼叫方無法偽造。value 過大時會被截斷並標上 truncated。',
  },
  {
    path: '$sys/viewport',
    kind: 'state',
    access: 'r',
    schema: {
      type: 'object',
      properties: { w: { type: 'number' }, h: { type: 'number' } },
      required: ['w', 'h'],
    },
    example: { w: 1440, h: 900 },
    description:
      '桌面可用區域的像素尺寸，會隨視窗縮放更新。要自行計算視窗位置時以它為基準；但要把視窗放進版面格子，請直接用 $sys/cmd/snap，不需要自己算像素。',
  },
  {
    path: '$sys/layout/templates',
    kind: 'state',
    access: 'r',
    schema: { type: 'array' },
    description:
      '可選的版面板型清單，每一項含 id、標題與格子定義。格子座標是 0–1 的比例，因此在任何螢幕尺寸下都成立。',
  },
  {
    path: '$sys/layout/template',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string' },
    example: 'grid3x3',
    description:
      '目前選用的版面板型 id，必須是 $sys/layout/templates 裡的其中一個。選定之後，拖曳或縮放視窗放開時會自動吸附到最近的格子。設為 "free" 表示不吸附。',
  },
  {
    path: '$sys/cmd/snap',
    kind: 'action',
    access: 'w',
    schema: {
      type: 'object',
      properties: {
        uid: { type: 'string' },
        cell: { type: 'integer', minimum: 0 },
        template: { type: 'string' },
      },
      required: ['uid'],
      additionalProperties: false,
    },
    example: { uid: 'hr_7f3a2c', cell: 0 },
    description:
      '把某個視窗放進版面格子。cell 是格子索引（左上為 0，由左而右、由上而下）；省略時吸附到離視窗中心最近的格子。template 省略時使用目前選用的板型。回傳實際套用的 {rect, cell}。這比自己算像素可靠——格子定義會隨螢幕尺寸自動換算。',
  },
  {
    path: '$sys/visible',
    kind: 'state',
    access: 'r',
    schema: { type: 'boolean' },
    description:
      '桌面現在看不看得見。false 表示分頁被切走或最小化，此時**瀏覽器會節流計時器**——setTimeout / setInterval 會被壓到最少 1 秒、五分鐘後變成一分鐘一次，requestAnimationFrame 完全停止。網路事件（WebSocket / SSE）不受影響。看不見的時候不要依賴輪詢，改用推播；需要保證完成的工作不該留在瀏覽器裡。',
  },
  {
    path: '$sys/menu/at',
    kind: 'state',
    access: 'rw',
    schema: {
      type: 'object',
      nullable: true,
      properties: { x: { type: 'number' }, y: { type: 'number' } },
      required: ['x', 'y'],
      additionalProperties: false,
    },
    example: { x: 420, y: 260 },
    description:
      '人在桌布空白處按下右鍵的位置，像素座標；null 表示沒有人在等選單。桌面**只回報這件事發生了**，不決定要跳出什麼——想提供右鍵選單的 app 訂閱這個節點，自己決定要顯示什麼、顯示在哪。寫入座標等同於「就當作有人在這裡按了右鍵」，寫 null 表示收起，兩者 app 與 agent 都做得到。',
  },
  {
    path: '$sys/gate/level',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string', enum: ['off', 'high', 'low'] },
    example: 'high',
    description:
      '風險閘門的門檻。"off" 不攔截；"high" 只攔 risk 為 high 的操作；"low" 連 low 也攔。被攔下的寫入或呼叫會排進 $sys/gate/pending 等人確認，在確認之前不會生效。這只影響 app 發起的操作——桌面自己的動作與 $sys/gate/exempt 裡的來源不受影響。',
  },
  {
    path: '$sys/gate/exempt',
    kind: 'state',
    access: 'rw',
    schema: { type: 'array', items: { type: 'string' } },
    example: ['shell'],
    description:
      '免確認的來源清單，內容是實例 uid。人直接操作介面時桌面用的來源是 "shell"——人按下按鈕本身就是同意，不該再問一次。',
  },
  {
    path: '$sys/gate/timeout_ms',
    kind: 'state',
    access: 'rw',
    schema: { type: 'integer', minimum: 1000, maximum: 3600000 },
    unit: 'ms',
    example: 120000,
    description:
      '等待人類確認的上限。逾時的操作視同未執行，發起者會收到 E_TIMEOUT。注意被攔下的操作以這個時間為準，不是呼叫方自己指定的逾時。',
  },
  {
    path: '$sys/gate/pending',
    kind: 'state',
    access: 'r',
    schema: { type: 'array' },
    description:
      '正在等待人類確認的操作清單，每一項含 id、路徑、值、發起者、風險等級與節點說明。用 $sys/cmd/approve 或 $sys/cmd/deny 處理。清單是空的表示沒有東西被卡住。',
  },
  {
    path: '$sys/cmd/approve',
    kind: 'action',
    access: 'w',
    schema: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
      additionalProperties: false,
    },
    description:
      '同意一個被閘門攔下的操作，它會立刻繼續執行。id 來自 $sys/gate/pending。這個 action 本身永遠不會被閘門攔截，否則會鎖死。',
  },
  {
    path: '$sys/cmd/deny',
    kind: 'action',
    access: 'w',
    schema: {
      type: 'object',
      properties: { id: { type: 'string' }, reason: { type: 'string' } },
      required: ['id'],
      additionalProperties: false,
    },
    example: { id: 'g3', reason: '量測進行中，暫不調整' },
    description:
      '拒絕一個被閘門攔下的操作。發起者會收到 E_DENIED 與這裡給的 reason——請寫清楚原因，agent 會依它調整後續行為。',
  },
  {
    path: '$sys/screen',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string', nullable: true, pattern: '^[0-9]{2,5}x[0-9]{2,5}$' },
    example: '390x844',
    description:
      '螢幕尺寸——這個桌面被裝在多大的一塊玻璃上。`"390x844"` 這種寫法，**null 表示跟著瀏覽器視窗**（預設）。給定之後畫面上會出現一塊那麼大的螢幕（置中、四周留白），桌面的一切都住在裡面：dock、右鍵選單、面板都跟著縮到那塊螢幕裡，所以看到的就是那台裝置上真正會看到的樣子。**桌面本身沒有另一個尺寸**：它剛好是一個螢幕大，把視窗拖出邊界時自己長出去，捲過去就找得回來。',
  },
  {
    path: '$sys/locale',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string', enum: ['zh-Hant', 'en'] },
    description:
      '這個桌面的介面語言。**app 用訂閱的，不要自己去猜瀏覽器語言**——一個桌面上十個 iframe 各自猜，結果會是十個各說各話的介面。節點的 description 不隨它改變：那是寫給 agent 讀的，而模型不需要它翻譯。',
  },
  {
    path: '$sys/wallpaper/color',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string' },
    example: '#1a1a2e',
    description:
      '桌面背景。任何合法的 CSS background 值都可以，不限純色——漸層如 "linear-gradient(135deg,#0f2027,#2c5364)" 也成立。',
  },
  {
    path: '$sys/wallpaper/image',
    kind: 'state',
    access: 'rw',
    schema: { type: 'string', nullable: true },
    description: '桌面背景圖片的 URL。設為 null 表示不使用圖片，改用 $sys/wallpaper/color。',
  },
  {
    path: '$sys/cmd/launch',
    kind: 'action',
    access: 'w',
    risk: 'low',
    schema: {
      type: 'object',
      properties: {
        app_id: { type: 'string' },
        rect: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            w: { type: 'number', minimum: 0 },
            h: { type: 'number', minimum: 0 },
          },
          additionalProperties: false,
        },
        state: { type: 'string', enum: ['normal', 'min', 'max', 'hidden'] },
        z: { type: 'integer' },
        transparent: { type: 'boolean' },
      },
      required: ['app_id'],
      additionalProperties: false,
    },
    example: { app_id: 'timeline', rect: { x: 40, y: 40, w: 420, h: 600 } },
    description:
      '開啟一個新的 app 實例，回傳 {uid}。app_id 必須來自 $sys/registry/apps 目錄——不能指定任意網址。同一個 app_id 可以開多次，每次得到獨立的 uid。rect 等欄位可以直接指定要放在哪裡，省略就用目錄的預設；**要擺到特定位置請在這裡給，不要開完再改 $window/rect**——那會讓視窗先出現在別處再跳過去。開啟後請讀取 app/{uid}/$meta 了解它能做什麼。',
  },
  {
    path: '$sys/cmd/close',
    kind: 'action',
    access: 'w',
    risk: 'low',
    schema: {
      type: 'object',
      properties: { uid: { type: 'string' } },
      required: ['uid'],
      additionalProperties: false,
    },
    example: { uid: 'hr_7f3a2c' },
    description:
      '完全關閉一個實例，含其視窗。該實例的所有節點（包含 $window 與 $meta）都會消失。這與 app 自行結束不同——app 自行結束會留下視窗框顯示已關閉狀態。未存檔的內容可能遺失。',
  },
]
